/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import BigNumber from 'bignumber.js';
import { useMutation, useQuery } from 'react-query';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { currencies } from '../../../../utilities/constants';
import { MODAL_SCREENS } from '../../constants';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { getLoanDetailsHelper, getRequiredFurtherCollateral } from '../../helpers';
import { LOANS_ALL, LOAN_DETAILS } from '../../queries';
import { useUserWallets } from '../../../Accounts/hooks';
import { showAlert } from '../../../../utilities/notifications';
import { LOANS_PAY_COLLATERAL } from '../../mutations';
import FeatureItem from '../../../../components/FeatureItem';
import { USER_REGION_SETTINGS_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';

const LoanMoreCollateral = ({
  openScreen,
  loanMetaData,
  onHide,
  setExtraCollateralOptions,
  onExchangeClick,
  onReceiveClick,
}) => {
  const { t: translate } = useTranslation();

  const { data: loan } = useQuery([LOAN_DETAILS, loanMetaData.id]);

  const { isUS } = useUserPersonalInfo();

  const {
    ltv_percentage_initial: initialLTV,
    principal_currency: principalCurrency,
    collateral_currency: collateralCurrency,
    created_at: startedOnDate,
    loan_due_at: payOffDate,
  } = loanMetaData;

  const remainingInterests = formatNumber(
    BigNumber(loan?.interest_amount).minus(loan?.paid_interests),
    currencies[principalCurrency].decimal
  );

  const rate = useRate(collateralCurrency, principalCurrency);
  const {
    safeNetPrincipalAmount,
    displayedNetPrincipalAmount,
    safeCollateralAmount,
    formattedCurrentLTV: currentLTV,
  } = useMemo(() => getLoanDetailsHelper(loanMetaData, rate), [loanMetaData, rate]);

  const furtherCollateral = useMemo(
    () =>
      getRequiredFurtherCollateral({
        initialLTV,
        safeCollateralAmount,
        safeNetPrincipalAmount,
        rate,
      }),
    [initialLTV, safeCollateralAmount, safeNetPrincipalAmount, rate]
  );

  const collateralNeededRaw = furtherCollateral.toNumber();

  const [userWallets] = useUserWallets();

  const collateralSourceWallet = userWallets[collateralCurrency];

  const collateralSourceBalance = formatNumber(
    collateralSourceWallet?.available_balance * currencies[principalCurrency].conversion,
    currencies[collateralCurrency].decimal
  );

  const isCollateralSourceWalletHaveBalance = collateralSourceBalance > collateralNeededRaw;

  const { refetch: refetchAllLoans } = useQuery(LOANS_ALL, { enabled: false });

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const [allowExchangeSetting] = useSetting('app_features', 'feature_allow_exchange');

  const isExchangeAvailable = useMemo(
    () => allowExchangeSetting && (userRegionSettings?.can_exchange || false),
    [allowExchangeSetting, userRegionSettings]
  );

  const { mutate: payCollateral, isLoading } = useMutation(LOANS_PAY_COLLATERAL, {
    onSettled: (_, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        refetchAllLoans();
        showAlert({
          message: translate('add_collateral_success_msg').replace(
            '__AMOUNT__',
            `${formatNumber(
              collateralNeededRaw,
              currencies[collateralCurrency.toUpperCase()].decimal
            )} ${collateralCurrency.toUpperCase()}`
          ),
          type: 'success',
        });
        onHide();
      }
    },
  });

  const handleReceiveCrypto = () => {
    setExtraCollateralOptions({
      collateralWallet: collateralSourceWallet,
      minCollateralAmountNeeded: new BigNumber(collateralNeededRaw).minus(collateralSourceBalance),
    });
    onReceiveClick();
  };

  const renderFooter = () => (
    <button
      className={`btn ${isCollateralSourceWalletHaveBalance ? 'btn-primary' : 'btn-disabled'}`}
      loading={isLoading ? 'true' : 'false'}
      type="button"
      disabled={!isCollateralSourceWalletHaveBalance}
      onClick={() => payCollateral({ loanId: loan.id, amount: collateralNeededRaw })}>
      {translate('pay_now_btn_title')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack
      title={translate('loan_add_collateral_title')}
      renderFooter={renderFooter}
      onPrevClick={() => openScreen(MODAL_SCREENS.LOAN_DETAILS)}>
      <div className="rounded-extra bg-light mt-3 card-calculator__summary">
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_add_collateral_agreed_LTV')}
              </p>
              <b className="text-f6">{`${initialLTV}%`}</b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_add_collateral_current_LTV')}
              </p>
              <b className="text-f6">{`${currentLTV}%`}</b>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <b className="text-primary text-f4">{`${formatNumber(
          collateralNeededRaw,
          currencies[collateralCurrency.toUpperCase()].decimal
        )} ${collateralCurrency.toUpperCase()}`}</b>
      </div>

      <p className="text-shadow text-f5">{translate('loan_add_collateral_agreed_LTV_note')}</p>
      <div className="rounded-extra bg-light mt-4 card-calculator__summary">
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_add_collateral_started_on')}
              </p>
              <b className="text-f6">{moment(startedOnDate).format('l')}</b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_review_offer_pay_off_date_label')}
              </p>
              <b className="text-f6">
                {moment(payOffDate).format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY')}
              </b>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-extra bg-light mt-3 card-calculator__summary">
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_add_collateral_cryptodraft_value')}
              </p>
              <b className="text-f6">{`${displayedNetPrincipalAmount} ${principalCurrency.toUpperCase()}`}</b>
            </div>
          </div>
          <hr className="m-0" />
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="card-calculator__summary--item-note text-shadow text-f6">
                {translate('loan_add_collateral_total_interest_remaining')}
              </p>
              <b className="text-f6">{`${remainingInterests} ${principalCurrency.toUpperCase()}`}</b>
            </div>
          </div>
        </div>
      </div>
      {!isCollateralSourceWalletHaveBalance && (
        <div className="alert alert-danger alert-action cursor-pointer mt-4 py-3 mb-0" role="alert">
          <i className="text-danger icon icon-cancel-icon pe-none" />
          <span className="content text-start pe-none">
            <p className="m-0">
              {translate('loan_add_collateral_more_crypto_note').replace(
                '__CRYPTO__',
                currencies[collateralCurrency.toUpperCase()].fullName
              )}
            </p>
          </span>
        </div>
      )}
      <div className="bg-light rounded-extra p-3 d-flex flex-row mt-2">
        <div className="d-flex flex-row align-items-center justify-content-between">
          <i className={`icon-${collateralCurrency.toUpperCase()} m-2`} />
          <b>{collateralCurrency.toUpperCase()}</b>
        </div>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {`${collateralSourceBalance} ${collateralCurrency.toUpperCase()}`}
          </b>
          <p className="text-shadow text-f6 m-0">{translate('available_balance_label')}</p>
        </span>
      </div>
      {!isCollateralSourceWalletHaveBalance && (
        <>
          <FeatureItem
            className="mb-1"
            label={translate('receive_btn_label').replace(
              '__CURRENCY__',
              collateralCurrency.toUpperCase()
            )}
            subtitle={translate('transfer_btn_sublabel').replace(
              '__CURRENCY__',
              collateralCurrency.toUpperCase()
            )}
            icon="icon-circle-card"
            dataTestid="transfer-balance"
            onClick={handleReceiveCrypto}>
            <p>
              {translate('receive_btn_label').replace(
                '__CURRENCY__',
                collateralCurrency.toUpperCase()
              )}
            </p>
            <p className="text-f6">
              {translate('transfer_btn_sublabel').replace(
                '__CURRENCY__',
                collateralCurrency.toUpperCase()
              )}
            </p>
          </FeatureItem>
          <FeatureItem
            disabled={!isExchangeAvailable}
            className="mb-1"
            label={translate('exchange_btn_label')}
            subtitle={translate('exchange_btn_sublabel').replace(
              '__CURRENCY__',
              collateralCurrency.toUpperCase()
            )}
            icon="icon-buy-sell-menu"
            dataTestid="exchange"
            onClick={() => {
              onHide();
              onExchangeClick();
            }}>
            <p>{translate('exchange_btn_label')}</p>
            <p className="text-f6">
              {translate('exchange_btn_sublabel').replace(
                '__CURRENCY__',
                collateralCurrency.toUpperCase()
              )}
            </p>
          </FeatureItem>
        </>
      )}
    </ModalBodyLayout>
  );
};

export default LoanMoreCollateral;

LoanMoreCollateral.propTypes = {
  openScreen: PropTypes.func.isRequired,
  loanMetaData: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  setExtraCollateralOptions: PropTypes.func.isRequired,
  onExchangeClick: PropTypes.func.isRequired,
  onReceiveClick: PropTypes.func.isRequired,
};

LoanMoreCollateral.defaultProps = {
  loanMetaData: null,
};
